import { useMutation, useQuery } from "@apollo/client/react"
import {
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  TouchSensor,
  closestCenter,
  useSensor,
  useSensors,
} from "@dnd-kit/core"
import {
  SortableContext,
  arrayMove,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable"
import { CSS } from "@dnd-kit/utilities"
import { GripVertical, Pencil, Plus, Star, Trash2 } from "lucide-react"
import React, { useEffect, useMemo, useState } from "react"
import { toast } from "sonner"
import { gql } from "~/__generated__"
import { cn } from "~/common/cn"
import { useDocumentTitle } from "~/common/use-document-title"
import { GraphqlError } from "~/ui/graphql-error"
import { Sheet, SheetContent, SheetTitle } from "~/ui/sheet"

const SEARCH_KEYWORDS_QUERY = gql(/* GraphQL */ `
  query SearchKeywords {
    searchKeywords {
      id
      keyword
      active
      position
      createdAt
      activeListingCount
      auctionNinjaCount
      ctbidsCount
      recentListings {
        id
        title
        price
        photoUrl
        listingUrl
        endsAt
        location
        source
      }
    }
  }
`)

const SEARCH_KEYWORD_CREATE_MUTATION = gql(/* GraphQL */ `
  mutation SearchKeywordCreate($input: SearchKeywordCreateInput!) {
    searchKeywordCreate(input: $input) {
      searchKeyword {
        id
        keyword
        active
        position
        createdAt
        activeListingCount
        auctionNinjaCount
        ctbidsCount
        recentListings {
          id
          title
          price
          photoUrl
          listingUrl
          endsAt
          location
          source
        }
      }
    }
  }
`)

const SEARCH_KEYWORD_DELETE_MUTATION = gql(/* GraphQL */ `
  mutation SearchKeywordDelete($input: SearchKeywordDeleteInput!) {
    searchKeywordDelete(input: $input) {
      success
    }
  }
`)

const SEARCH_KEYWORD_REORDER_MUTATION = gql(/* GraphQL */ `
  mutation SearchKeywordReorder($input: SearchKeywordReorderInput!) {
    searchKeywordReorder(input: $input) {
      success
    }
  }
`)

const SEARCH_KEYWORD_UPDATE_MUTATION = gql(/* GraphQL */ `
  mutation SearchKeywordUpdate($input: SearchKeywordUpdateInput!) {
    searchKeywordUpdate(input: $input) {
      searchKeyword {
        id
        keyword
      }
    }
  }
`)

const WATCHED_LISTINGS_QUERY = gql(/* GraphQL */ `
  query WatchedListings {
    watchedListings {
      id
      listing {
        id
      }
    }
  }
`)

const WATCHED_LISTING_CREATE_MUTATION = gql(/* GraphQL */ `
  mutation WatchedListingCreate($input: WatchedListingCreateInput!) {
    watchedListingCreate(input: $input) {
      watchedListing {
        id
        listing {
          id
        }
      }
    }
  }
`)

const WATCHED_LISTING_DELETE_MUTATION = gql(/* GraphQL */ `
  mutation WatchedListingDelete($input: WatchedListingDeleteInput!) {
    watchedListingDelete(input: $input) {
      success
    }
  }
`)

type Listing = {
  id: string
  title?: string | null
  price?: string | null
  photoUrl?: string | null
  listingUrl?: string | null
  endsAt?: string | null
  location?: string | null
  source: string
}

type Keyword = {
  id: string
  keyword: string
  active: boolean
  position?: number | null
  createdAt: string
  activeListingCount: number
  auctionNinjaCount: number
  ctbidsCount: number
  recentListings: Listing[]
}

type SortMode = "custom" | "date_added" | "a_z" | "z_a"

function sortKeywords(keywords: Keyword[], mode: SortMode): Keyword[] {
  const copy = [...keywords]
  switch (mode) {
    case "custom":
      return copy
    case "date_added":
      return copy.sort((a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime())
    case "a_z":
      return copy.sort((a, b) => a.keyword.localeCompare(b.keyword))
    case "z_a":
      return copy.sort((a, b) => b.keyword.localeCompare(a.keyword))
  }
}

function etaLabel(endsAt: string | null | undefined): string {
  if (!endsAt) return ""
  const diff = new Date(endsAt).getTime() - Date.now()
  if (diff <= 0) return "Ended"
  const h = Math.floor(diff / 3600000)
  if (h < 24) return `${h}h`
  return `${Math.floor(h / 24)}d`
}

function sourceBadgeLabel(source: string): string {
  if (source === "ctbids") return "CTBIDS"
  return "AUCTION NINJA"
}

function sourceBadgeColor(source: string): string {
  if (source === "ctbids") return "bg-emerald-800 text-stone-50"
  return "bg-stone-800 text-stone-50"
}

function ListingCard({
  listing,
  isWatched,
  watchedListingId,
  onWatch,
  onUnwatch,
}: {
  listing: Listing
  isWatched: boolean
  watchedListingId: string | null
  onWatch: (listingId: string) => void
  onUnwatch: (watchedListingId: string) => void
}) {
  return (
    <a
      href={listing.listingUrl ?? "#"}
      target="_blank"
      rel="noopener noreferrer"
      className="group block bg-white border border-stone-200 hover:border-stone-400 transition-colors overflow-hidden"
    >
      <div className="relative aspect-square bg-stone-100 overflow-hidden">
        {listing.photoUrl ? (
          <img
            src={listing.photoUrl}
            alt={listing.title ?? ""}
            className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-300"
          />
        ) : (
          <div className="w-full h-full flex items-center justify-center text-stone-300 text-4xl">
            ·
          </div>
        )}
        <button
          onClick={(e) => {
            e.preventDefault()
            e.stopPropagation()
            if (isWatched && watchedListingId) {
              onUnwatch(watchedListingId)
            } else {
              onWatch(listing.id)
            }
          }}
          className={cn(
            "absolute top-2 right-2 flex items-center justify-center w-7 h-7 transition-colors",
            isWatched
              ? "bg-amber-600 text-white"
              : "bg-white/80 text-stone-400 hover:text-amber-600"
          )}
          aria-label={isWatched ? "Remove from watchlist" : "Add to watchlist"}
        >
          <Star size={13} fill={isWatched ? "currentColor" : "none"} />
        </button>
      </div>
      <div className="p-2 sm:p-3 space-y-1">
        {listing.title && (
          <p className="text-xs text-stone-700 leading-snug line-clamp-2 font-medium">
            {listing.title}
          </p>
        )}
        <div className="flex items-center gap-1.5 flex-wrap">
          <span
            className={cn(
              "text-[9px] font-bold tracking-wide px-1.5 py-0.5",
              sourceBadgeColor(listing.source)
            )}
          >
            {sourceBadgeLabel(listing.source)}
          </span>
        </div>
        {listing.price && <p className="text-sm font-bold text-amber-700">{listing.price}</p>}
        <div className="flex items-center justify-between">
          {listing.endsAt && (
            <p className="text-[10px] text-stone-400 uppercase tracking-wide">
              TIME LEFT {etaLabel(listing.endsAt)}
            </p>
          )}
          {listing.location && (
            <p className="text-[10px] text-stone-400 truncate max-w-[120px]">{listing.location}</p>
          )}
        </div>
      </div>
    </a>
  )
}

function KeywordRowLeft({
  kw,
  isSelected,
  onSelect,
  onDelete,
  onEdit,
  dragListeners,
  isDraggable,
}: {
  kw: Keyword
  isSelected: boolean
  onSelect: (id: string) => void
  onDelete: (id: string) => void
  onEdit: (id: string, keyword: string) => void
  dragListeners?: Record<string, unknown>
  isDraggable: boolean
}) {
  const [editing, setEditing] = useState(false)
  const [editValue, setEditValue] = useState(kw.keyword)

  const commitEdit = () => {
    const trimmed = editValue.trim().toLowerCase()
    if (trimmed && trimmed !== kw.keyword) onEdit(kw.id, trimmed)
    else setEditValue(kw.keyword)
    setEditing(false)
  }

  const handleEditKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === "Enter") commitEdit()
    if (e.key === "Escape") {
      setEditValue(kw.keyword)
      setEditing(false)
    }
  }

  return (
    <div
      className={cn(
        "flex items-center gap-2 px-3 py-2.5 border-b border-stone-100 cursor-pointer transition-colors",
        isSelected ? "bg-stone-900" : "hover:bg-stone-50",
        !kw.active && "opacity-50"
      )}
      onClick={() => !editing && onSelect(kw.id)}
    >
      {isDraggable && (
        <button
          className={cn(
            "shrink-0 cursor-grab active:cursor-grabbing touch-none",
            isSelected ? "text-stone-400" : "text-stone-300 hover:text-stone-500"
          )}
          {...dragListeners}
          aria-label="Drag to reorder"
          onClick={(e) => e.stopPropagation()}
        >
          <GripVertical size={12} />
        </button>
      )}
      {editing ? (
        <input
          autoFocus
          value={editValue}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={commitEdit}
          onKeyDown={handleEditKeyDown}
          onClick={(e) => e.stopPropagation()}
          className="flex-1 min-w-0 text-xs font-medium border-b border-stone-400 bg-transparent outline-none py-0.5 text-stone-800"
        />
      ) : (
        <span
          className={cn(
            "flex-1 min-w-0 text-xs font-medium capitalize truncate",
            isSelected ? "text-stone-50" : "text-stone-800"
          )}
        >
          {kw.keyword}
        </span>
      )}
      <div className="flex items-center gap-1.5 shrink-0" onClick={(e) => e.stopPropagation()}>
        {kw.activeListingCount > 0 && (
          <span
            className={cn(
              "text-[9px] font-bold tracking-wide px-1.5 py-0.5",
              isSelected ? "bg-amber-600 text-white" : "bg-amber-100 text-amber-700"
            )}
          >
            {kw.activeListingCount}
          </span>
        )}
        {!editing && (
          <button
            onClick={() => {
              setEditValue(kw.keyword)
              setEditing(true)
            }}
            className={cn(
              "transition-colors",
              isSelected
                ? "text-stone-400 hover:text-stone-200"
                : "text-stone-300 hover:text-stone-600"
            )}
            aria-label="Edit"
          >
            <Pencil size={11} />
          </button>
        )}
        <button
          onClick={() => onDelete(kw.id)}
          className={cn(
            "transition-colors",
            isSelected ? "text-stone-400 hover:text-red-400" : "text-stone-300 hover:text-red-500"
          )}
          aria-label="Delete"
        >
          <Trash2 size={11} />
        </button>
      </div>
    </div>
  )
}

function SortableKeywordRowLeft({
  kw,
  isSelected,
  onSelect,
  onDelete,
  onEdit,
}: {
  kw: Keyword
  isSelected: boolean
  onSelect: (id: string) => void
  onDelete: (id: string) => void
  onEdit: (id: string, keyword: string) => void
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
    id: kw.id,
  })
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    zIndex: isDragging ? 10 : undefined,
    position: isDragging ? ("relative" as const) : undefined,
  }
  return (
    <div ref={setNodeRef} style={style} {...attributes}>
      <KeywordRowLeft
        kw={kw}
        isSelected={isSelected}
        onSelect={onSelect}
        onDelete={onDelete}
        onEdit={onEdit}
        dragListeners={listeners as Record<string, unknown>}
        isDraggable
      />
    </div>
  )
}

export const DashboardScreen: React.FC = () => {
  useDocumentTitle("Dashboard")

  const { data, loading, error, refetch } = useQuery(SEARCH_KEYWORDS_QUERY)
  const { data: watchedData, refetch: refetchWatched } = useQuery(WATCHED_LISTINGS_QUERY)
  const [searchKeywordCreate, { loading: createLoading }] = useMutation(
    SEARCH_KEYWORD_CREATE_MUTATION
  )
  const [searchKeywordDelete] = useMutation(SEARCH_KEYWORD_DELETE_MUTATION)
  const [searchKeywordReorder] = useMutation(SEARCH_KEYWORD_REORDER_MUTATION)
  const [searchKeywordUpdate] = useMutation(SEARCH_KEYWORD_UPDATE_MUTATION)
  const [watchedListingCreate] = useMutation(WATCHED_LISTING_CREATE_MUTATION)
  const [watchedListingDelete] = useMutation(WATCHED_LISTING_DELETE_MUTATION)

  const [newKeyword, setNewKeyword] = useState("")
  const [sortMode] = useState<SortMode>("custom")
  const [deletedIds, setDeletedIds] = useState<Set<string>>(new Set())
  const [activeOrder, setActiveOrder] = useState<string[] | null>(null)
  const [pausedOrder, setPausedOrder] = useState<string[] | null>(null)
  const [selectedKeywordId, setSelectedKeywordId] = useState<string | null>(null)
  const [sourceFilter, setSourceFilter] = useState<"all" | "auction_ninja" | "ctbids">("all")
  const [endingTodayOnly, setEndingTodayOnly] = useState(false)
  const [radarSettingsOpen, setRadarSettingsOpen] = useState(false)

  useEffect(() => {
    const handler = () => setRadarSettingsOpen(true)
    window.addEventListener("open-radar-settings", handler)
    return () => window.removeEventListener("open-radar-settings", handler)
  }, [])

  const watchedByListingId = useMemo(() => {
    const map = new Map<string, string>()
    for (const wl of watchedData?.watchedListings ?? []) {
      map.set(wl.listing.id, wl.id)
    }
    return map
  }, [watchedData])

  const handleWatch = async (listingId: string) => {
    await watchedListingCreate({
      variables: { input: { listingId } },
      onError: (err) => toast.error(err.message),
      onCompleted: () => refetchWatched(),
    })
  }

  const handleUnwatch = async (watchedListingId: string) => {
    await watchedListingDelete({
      variables: { input: { id: watchedListingId } },
      onError: (err) => toast.error(err.message),
      onCompleted: () => refetchWatched(),
    })
  }

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(TouchSensor, { activationConstraint: { delay: 250, tolerance: 5 } }),
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  )

  const serverKeywords = useMemo(
    () => (data?.searchKeywords ?? []) as Keyword[],
    [data?.searchKeywords]
  )

  const localKeywords = useMemo(() => {
    const filtered = serverKeywords.filter((k) => !deletedIds.has(k.id))
    const activeKws = filtered.filter((k) => k.active)
    const pausedKws = filtered.filter((k) => !k.active)
    const applyOrder = (kws: Keyword[], order: string[] | null) => {
      if (!order) return kws
      const byId = new Map(kws.map((k) => [k.id, k]))
      return order.flatMap((id) => (byId.has(id) ? [byId.get(id)!] : []))
    }
    return [...applyOrder(activeKws, activeOrder), ...applyOrder(pausedKws, pausedOrder)]
  }, [serverKeywords, deletedIds, activeOrder, pausedOrder])

  const sorted = sortKeywords(localKeywords, sortMode)
  const activeKeywords = sorted.filter((k) => k.active)

  const allListings = useMemo(() => {
    const kwsToShow = selectedKeywordId
      ? localKeywords.filter((k) => k.id === selectedKeywordId)
      : localKeywords.filter((k) => k.active)
    const flat = kwsToShow.flatMap((k) => k.recentListings)
    const unique = Array.from(new Map(flat.map((l) => [l.id, l])).values())
    return unique.sort((a, b) => {
      if (!a.endsAt) return 1
      if (!b.endsAt) return -1
      return new Date(a.endsAt).getTime() - new Date(b.endsAt).getTime()
    })
  }, [localKeywords, selectedKeywordId])

  const filteredListings = useMemo(() => {
    let result = allListings
    if (sourceFilter !== "all") result = result.filter((l) => l.source === sourceFilter)
    if (endingTodayOnly) {
      const endOfDay = new Date()
      endOfDay.setHours(23, 59, 59, 999)
      result = result.filter((l) => l.endsAt && new Date(l.endsAt) <= endOfDay)
    }
    return result
  }, [allListings, sourceFilter, endingTodayOnly])

  const activeLocations = useMemo(() => {
    const locs = allListings.flatMap((l) => (l.location ? [l.location] : []))
    return Array.from(new Set(locs)).slice(0, 8)
  }, [allListings])

  const totalActiveCount = useMemo(
    () => localKeywords.filter((k) => k.active).reduce((sum, k) => sum + k.activeListingCount, 0),
    [localKeywords]
  )

  const liveResultsCount = useMemo(() => {
    if (endingTodayOnly) return filteredListings.length
    const kwsToShow = selectedKeywordId
      ? localKeywords.filter((k) => k.id === selectedKeywordId)
      : localKeywords.filter((k) => k.active)
    if (sourceFilter === "auction_ninja")
      return kwsToShow.reduce((sum, k) => sum + k.auctionNinjaCount, 0)
    if (sourceFilter === "ctbids") return kwsToShow.reduce((sum, k) => sum + k.ctbidsCount, 0)
    return kwsToShow.reduce((sum, k) => sum + k.activeListingCount, 0)
  }, [localKeywords, selectedKeywordId, sourceFilter, endingTodayOnly, filteredListings.length])

  const handleAdd = async (e: React.FormEvent) => {
    e.preventDefault()
    const terms = newKeyword
      .split(",")
      .map((t) => t.trim().toLowerCase())
      .filter(Boolean)
    if (terms.length === 0) return
    const existing = localKeywords.map((k) => k.keyword)
    const duplicates = terms.filter((t) => existing.includes(t))
    const newTerms = terms.filter((t) => !existing.includes(t))
    if (duplicates.length > 0) toast.warning(`Already tracking: ${duplicates.join(", ")}`)
    if (newTerms.length === 0) return
    setNewKeyword("")
    for (const term of newTerms) {
      await searchKeywordCreate({
        variables: { input: { keyword: term } },
        onError: (err) => toast.error(err.message),
      })
    }
    await refetch()
    setActiveOrder(null)
    setPausedOrder(null)
    toast.success(
      newTerms.length === 1 ? `"${newTerms[0]}" added` : `${newTerms.length} keywords added`
    )
  }

  const handleDelete = async (id: string) => {
    setDeletedIds((prev) => new Set([...prev, id]))
    if (selectedKeywordId === id) setSelectedKeywordId(null)
    await searchKeywordDelete({
      variables: { input: { id } },
      onError: (err) => {
        toast.error(err.message)
        setDeletedIds((prev) => {
          const next = new Set(prev)
          next.delete(id)
          return next
        })
      },
    })
  }

  const handleEdit = async (id: string, keyword: string) => {
    await searchKeywordUpdate({
      variables: { input: { id, keyword } },
      onError: (err) => toast.error(err.message),
      onCompleted: () => refetch(),
    })
  }

  const handleReorder = async (newOrder: Keyword[]) => {
    setActiveOrder(newOrder.map((k) => k.id))
    await searchKeywordReorder({
      variables: { input: { ids: newOrder.map((k) => k.id) } },
      onError: (err) => {
        toast.error(err.message)
        setActiveOrder(null)
      },
    })
  }

  function handleDragEnd(event: DragEndEvent) {
    const { active, over } = event
    if (!over || active.id === over.id) return
    const oldIndex = activeKeywords.findIndex((k) => k.id === active.id)
    const newIndex = activeKeywords.findIndex((k) => k.id === over.id)
    handleReorder(arrayMove(activeKeywords, oldIndex, newIndex))
  }

  if (error) return <GraphqlError error={error} />

  return (
    <div className="flex flex-1 min-h-0 h-full">
      {/* ── Left panel: Search Settings ─────────────────────────────────── */}
      <div className="hidden md:flex w-[280px] shrink-0 flex-col border-r border-stone-200 bg-white">
        {/* Header + add */}
        <div className="px-4 pt-5 pb-3 border-b border-stone-100">
          <div className="flex items-center justify-between mb-3">
            <p className="text-[9px] font-semibold tracking-[3px] uppercase text-stone-400">
              Search Settings
            </p>
            <form onSubmit={handleAdd} className="flex items-center gap-1">
              <input
                value={newKeyword}
                onChange={(e) => setNewKeyword(e.target.value)}
                placeholder="Add keyword…"
                className="w-28 text-[11px] border border-stone-200 bg-stone-50 px-2 py-1 outline-none focus:border-stone-400 placeholder:text-stone-400"
              />
              <button
                type="submit"
                disabled={createLoading || !newKeyword.trim()}
                className="flex items-center justify-center w-6 h-6 bg-stone-900 text-stone-50 hover:bg-stone-700 disabled:opacity-40 transition-colors"
                aria-label="Add keyword"
              >
                <Plus size={12} />
              </button>
            </form>
          </div>
        </div>

        {/* Keyword list */}
        <div className="flex-1 overflow-y-auto">
          {loading && <p className="px-4 py-3 text-xs text-stone-400">Loading…</p>}
          {!loading && activeKeywords.length === 0 && (
            <p className="px-4 py-3 text-xs text-stone-400">No keywords yet. Add one above.</p>
          )}

          {/* All keywords button */}
          {activeKeywords.length > 0 && (
            <div
              className={cn(
                "flex items-center gap-2 px-3 py-2.5 border-b border-stone-100 cursor-pointer transition-colors",
                selectedKeywordId === null ? "bg-stone-900" : "hover:bg-stone-50"
              )}
              onClick={() => setSelectedKeywordId(null)}
            >
              <span
                className={cn(
                  "flex-1 text-xs font-medium",
                  selectedKeywordId === null ? "text-stone-50" : "text-stone-600"
                )}
              >
                All keywords
              </span>
              <span
                className={cn(
                  "text-[9px] font-bold px-1.5 py-0.5",
                  selectedKeywordId === null
                    ? "bg-amber-600 text-white"
                    : "bg-stone-100 text-stone-500"
                )}
              >
                {totalActiveCount}
              </span>
            </div>
          )}

          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={activeKeywords.map((k) => k.id)}
              strategy={verticalListSortingStrategy}
            >
              {activeKeywords.map((kw) => (
                <SortableKeywordRowLeft
                  key={kw.id}
                  kw={kw}
                  isSelected={selectedKeywordId === kw.id}
                  onSelect={setSelectedKeywordId}
                  onDelete={handleDelete}
                  onEdit={handleEdit}
                />
              ))}
            </SortableContext>
          </DndContext>
        </div>

        {/* Active Locations */}
        {activeLocations.length > 0 && (
          <div className="border-t border-stone-100 px-4 py-3">
            <p className="text-[9px] font-semibold tracking-[3px] uppercase text-stone-400 mb-2">
              Active Locations
            </p>
            <div className="space-y-1">
              {activeLocations.map((loc) => (
                <p key={loc} className="text-[11px] text-stone-500 truncate">
                  {loc}
                </p>
              ))}
            </div>
          </div>
        )}
      </div>

      {/* ── Right panel: Live Results ──────────────────────────────── */}
      <div className="flex flex-1 flex-col min-w-0 bg-stone-50">
        {/* Mobile: keyword chips + Search Settings opener */}
        <div className="md:hidden flex items-center gap-2 px-3 py-2 bg-white border-b border-stone-200">
          <div className="flex-1 flex gap-1.5 overflow-x-auto py-0.5 [&::-webkit-scrollbar]:hidden [-ms-overflow-style:none] [scrollbar-width:none]">
            <button
              onClick={() => setSelectedKeywordId(null)}
              className={cn(
                "shrink-0 px-2.5 py-1 text-[11px] border transition-colors whitespace-nowrap",
                selectedKeywordId === null
                  ? "bg-stone-900 text-stone-50 border-stone-900"
                  : "text-stone-500 border-stone-200"
              )}
            >
              All ({totalActiveCount})
            </button>
            {activeKeywords.map((kw) => (
              <button
                key={kw.id}
                onClick={() => setSelectedKeywordId(kw.id)}
                className={cn(
                  "shrink-0 px-2.5 py-1 text-[11px] border transition-colors whitespace-nowrap capitalize",
                  selectedKeywordId === kw.id
                    ? "bg-stone-900 text-stone-50 border-stone-900"
                    : "text-stone-500 border-stone-200"
                )}
              >
                {kw.keyword}
                {kw.activeListingCount > 0 && ` (${kw.activeListingCount})`}
              </button>
            ))}
          </div>
        </div>

        {/* Search Settings bottom sheet (mobile) */}
        <Sheet open={radarSettingsOpen} onOpenChange={setRadarSettingsOpen}>
          <SheetContent
            side="bottom"
            className="h-[75vh] p-0 flex flex-col rounded-t-2xl overflow-hidden"
          >
            <SheetTitle className="sr-only">Search Settings</SheetTitle>
            <div className="px-4 pt-5 pb-3 border-b border-stone-100">
              <div className="flex items-center justify-between">
                <p className="text-[9px] font-semibold tracking-[3px] uppercase text-stone-400">
                  Search Settings
                </p>
                <form onSubmit={handleAdd} className="flex items-center gap-1">
                  <input
                    value={newKeyword}
                    onChange={(e) => setNewKeyword(e.target.value)}
                    placeholder="Add keyword…"
                    className="w-28 text-[11px] border border-stone-200 bg-stone-50 px-2 py-1 outline-none focus:border-stone-400 placeholder:text-stone-400"
                  />
                  <button
                    type="submit"
                    disabled={createLoading || !newKeyword.trim()}
                    className="flex items-center justify-center w-6 h-6 bg-stone-900 text-stone-50 hover:bg-stone-700 disabled:opacity-40 transition-colors"
                    aria-label="Add keyword"
                  >
                    <Plus size={12} />
                  </button>
                </form>
              </div>
            </div>
            <div className="flex-1 overflow-y-auto">
              {loading && <p className="px-4 py-3 text-xs text-stone-400">Loading…</p>}
              {!loading && activeKeywords.length === 0 && (
                <p className="px-4 py-3 text-xs text-stone-400">No keywords yet. Add one above.</p>
              )}
              {activeKeywords.length > 0 && (
                <div
                  className={cn(
                    "flex items-center gap-2 px-3 py-2.5 border-b border-stone-100 cursor-pointer transition-colors",
                    selectedKeywordId === null ? "bg-stone-900" : "hover:bg-stone-50"
                  )}
                  onClick={() => {
                    setSelectedKeywordId(null)
                    setRadarSettingsOpen(false)
                  }}
                >
                  <span
                    className={cn(
                      "flex-1 text-xs font-medium",
                      selectedKeywordId === null ? "text-stone-50" : "text-stone-600"
                    )}
                  >
                    All keywords
                  </span>
                  <span
                    className={cn(
                      "text-[9px] font-bold px-1.5 py-0.5",
                      selectedKeywordId === null
                        ? "bg-amber-600 text-white"
                        : "bg-stone-100 text-stone-500"
                    )}
                  >
                    {totalActiveCount}
                  </span>
                </div>
              )}
              <DndContext
                sensors={sensors}
                collisionDetection={closestCenter}
                onDragEnd={handleDragEnd}
              >
                <SortableContext
                  items={activeKeywords.map((k) => k.id)}
                  strategy={verticalListSortingStrategy}
                >
                  {activeKeywords.map((kw) => (
                    <SortableKeywordRowLeft
                      key={kw.id}
                      kw={kw}
                      isSelected={selectedKeywordId === kw.id}
                      onSelect={(id) => {
                        setSelectedKeywordId(id)
                        setRadarSettingsOpen(false)
                      }}
                      onDelete={handleDelete}
                      onEdit={handleEdit}
                    />
                  ))}
                </SortableContext>
              </DndContext>
            </div>
            {activeLocations.length > 0 && (
              <div className="border-t border-stone-100 px-4 py-3">
                <p className="text-[9px] font-semibold tracking-[3px] uppercase text-stone-400 mb-2">
                  Active Locations
                </p>
                <div className="space-y-1">
                  {activeLocations.map((loc) => (
                    <p key={loc} className="text-[11px] text-stone-500 truncate">
                      {loc}
                    </p>
                  ))}
                </div>
              </div>
            )}
          </SheetContent>
        </Sheet>

        {/* Filter pills */}
        <div className="flex items-center gap-2 px-6 py-3 border-b border-stone-200 bg-white">
          {(
            [
              { label: "ALL SOURCES", value: "all" },
              { label: "AUCTION NINJA", value: "auction_ninja" },
              { label: "CTBIDS", value: "ctbids" },
            ] as const
          ).map(({ label, value }) => (
            <button
              key={value}
              onClick={() => setSourceFilter(value)}
              className={cn(
                "px-3 py-1 text-[11px] tracking-wide border transition-colors",
                sourceFilter === value
                  ? "bg-stone-900 text-stone-50 border-stone-900"
                  : "text-stone-500 border-stone-200 hover:border-stone-400 hover:text-stone-800"
              )}
            >
              {label}
            </button>
          ))}
          <button
            onClick={() => setEndingTodayOnly((v) => !v)}
            className={cn(
              "px-3 py-1 text-[11px] tracking-wide border transition-colors",
              endingTodayOnly
                ? "bg-amber-700 text-white border-amber-700"
                : "text-stone-500 border-stone-200 hover:border-stone-400 hover:text-stone-800"
            )}
          >
            ENDING TODAY
          </button>
        </div>

        {/* Results grid */}
        <div className="flex-1 overflow-y-auto px-3 py-4 sm:px-6 sm:py-5">
          <div className="flex items-baseline gap-3 mb-4">
            <h2
              className="text-sm font-bold text-stone-900"
              style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
            >
              Live Results
            </h2>
            {liveResultsCount > 0 && (
              <span className="text-[11px] text-stone-400">
                {liveResultsCount} listing{liveResultsCount !== 1 ? "s" : ""}
              </span>
            )}
          </div>

          {loading && <p className="text-sm text-stone-400">Loading…</p>}

          {!loading && filteredListings.length === 0 && (
            <div className="text-center py-16 border border-dashed border-stone-200">
              <p className="text-stone-400 text-sm">No results yet.</p>
              <p className="text-stone-400 text-sm mt-1">
                Add keywords to start seeing live listings.
              </p>
            </div>
          )}

          <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-3 gap-2 sm:gap-4">
            {filteredListings.map((listing) => (
              <ListingCard
                key={listing.id}
                listing={listing}
                isWatched={watchedByListingId.has(listing.id)}
                watchedListingId={watchedByListingId.get(listing.id) ?? null}
                onWatch={handleWatch}
                onUnwatch={handleUnwatch}
              />
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}
